// Impersonation-v1 (2026-06-21) — frontend token + meta storage.
//
// When a super-admin clicks "View as this user" the backend mints a
// short-lived JWT whose subject is the target user. That token (plus
// some display metadata so we can render a banner without re-fetching
// /api/me on every page load) lives here under localStorage keys that
// are deliberately distinct from the admin's regular session token at
// 'token'. The impersonation tab uses an axios interceptor (wired in
// AuthContext) to send THIS token in the Authorization header instead
// of the session token, while the original admin's tab keeps using the
// session token at 'token' untouched.
//
// Why localStorage and not sessionStorage:
// the impersonation tab is opened by window.open(...) from the original
// admin tab — sessionStorage is isolated per tab, so the new tab would
// boot without the token. localStorage is shared across tabs of the
// same origin, which is exactly what we want here.

export const IMPERSONATION_KEY = "solara.impersonation.token";
export const IMPERSONATION_META_KEY = "solara.impersonation.meta";

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage can throw (private mode, quota) — swallow silently;
    // impersonation simply won't persist across reloads in that case.
  }
}

function safeRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

export function getImpersonationToken() {
  return safeGet(IMPERSONATION_KEY) || null;
}

export function getImpersonationMeta() {
  const raw = safeGet(IMPERSONATION_META_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setImpersonation(token, meta) {
  if (!token) return;
  safeSet(IMPERSONATION_KEY, token);
  safeSet(IMPERSONATION_META_KEY, JSON.stringify(meta || {}));
}

export function clearImpersonation() {
  safeRemove(IMPERSONATION_KEY);
  safeRemove(IMPERSONATION_META_KEY);
}

export function isImpersonating() {
  return Boolean(getImpersonationToken());
}

// Helpers for the URL hand-off (button → window.open(...?impersonation_token=…))
// We base64-encode the meta blob so the URL is opaque to a casual reader
// AND survives URL encoding of nested JSON quotes.
export function encodeImpersonationMeta(meta) {
  try {
    const json = JSON.stringify(meta || {});
    // btoa only handles latin-1; encode UTF-8 first.
    return btoa(unescape(encodeURIComponent(json)));
  } catch {
    return "";
  }
}

export function decodeImpersonationMeta(b64) {
  if (!b64) return null;
  try {
    const json = decodeURIComponent(escape(atob(b64)));
    return JSON.parse(json);
  } catch {
    return null;
  }
}
